/**
 * Help Center — the liquid-glass guide browser + feedback panel.
 *
 * Left rail: search + section list + "Report a problem" (+ admin "New article").
 * Right pane: browse a section's articles, read an article (rendered Markdown),
 * send feedback, or (admin) create/edit an article.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useHelpStore } from '@/state/helpStore.js';
import { useAuthStore } from '@/state/authStore.js';
import { useToast } from '@/components/ui/useToast.js';
import {
  deleteHelpArticle,
  resetHelpArticle,
  getHelpArticle,
  getHelpArticles,
  getHelpSections,
  searchHelp
} from '@/api/endpoints/help.js';
import AskOmni from './AskOmni.jsx';
import Markdown from './Markdown.jsx';
import FeedbackForm from './FeedbackForm.jsx';
import ArticleEditor from './ArticleEditor.jsx';
import { useConfirm } from '@/components/ui/useConfirm.js';

// Article titles are admin-authored text, escaped so a confirm dialog shows
// the real title rather than dropping part of it (review finding H7).
import { escapeHtml } from '@/utils/dom.js';

export default function HelpCenter() {
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const confirm = useConfirm();
  const toast = useToast();
  const { view, section, articleId, feedbackType, close, goToSection, goToArticle, goToFeedback, setFeedbackType } =
    useHelpStore();

  const [sections, setSections] = useState([]);
  const [articles, setArticles] = useState([]);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null); // null = not searching
  const [editing, setEditing] = useState(null); // null | { article } | { create: true }
  const searchRef = useRef(0);

  const activeSection = section || sections[0]?.id || 'getting-started';
  const sectionMeta = sections.find((s) => s.id === activeSection);

  // Load sections once.
  useEffect(() => {
    let alive = true;
    getHelpSections()
      .then((r) => {
        if (alive) setSections(r?.sections || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Load the active section's articles when browsing.
  const loadArticles = useCallback(
    (sec) => {
      setLoading(true);
      getHelpArticles({ section: sec, all: isAdmin })
        .then((r) => setArticles(r?.articles || []))
        .catch(() => setArticles([]))
        .finally(() => setLoading(false));
    },
    [isAdmin]
  );

  useEffect(() => {
    if (view === 'browse' && !results) loadArticles(activeSection);
  }, [view, activeSection, results, loadArticles]);

  // Load the open article.
  useEffect(() => {
    if (view !== 'article' || !articleId) return;
    setLoading(true);
    setArticle(null);
    getHelpArticle(articleId)
      .then((r) => setArticle(r?.article || null))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [view, articleId]);

  // Debounced search across all sections.
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults(null);
      return undefined;
    }
    const id = ++searchRef.current;
    const t = setTimeout(() => {
      searchHelp(q)
        .then((r) => {
          if (id === searchRef.current) setResults(r?.results || []);
        })
        .catch(() => {
          if (id === searchRef.current) setResults([]);
        });
    }, 220);
    return () => clearTimeout(t);
  }, [query]);

  // Escape closes.
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') close();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  function openArticleFromResult(r) {
    setQuery('');
    setResults(null);
    goToArticle(r.id, r.section);
  }

  function removeArticle(a) {
    confirm({
      title: 'Delete this guide?',
      message:
        `<strong>${escapeHtml(a.title)}</strong> will be removed.` +
        (a.builtIn
          ? '<br><br>It is one of the guides shipped with Omni, so a future update can restore it.'
          : '<br><br>It was written here, so this cannot be undone.'),
      confirmText: 'Delete',
      type: 'danger',
      onConfirm: async () => {
        try {
          await deleteHelpArticle(a.id);
          toast.success('Deleted', `"${a.title}" has been removed.`);
          goToSection(a.section);
        } catch (err) {
          toast.error('Could not delete', err?.message || 'Please try again.');
        }
      }
    });
  }

  /**
   * Restore a shipped guide to the text in this build.
   *
   * Editing a built-in guide stops it receiving improvements — the seeder
   * deliberately never overwrites anyone's work — so this is how an admin
   * opts back in after a customisation is no longer wanted.
   */
  function resetArticle(a) {
    confirm({
      title: "Restore Omni's version?",
      message:
        `<strong>${escapeHtml(a.title)}</strong> will be replaced with the version shipped in this build, ` +
        'and any changes made here will be lost.' +
        '<br><br>It will also start picking up future improvements again automatically.',
      confirmText: 'Restore',
      type: 'warning',
      onConfirm: async () => {
        try {
          const res = await resetHelpArticle(a.id);
          toast.success('Guide restored', res?.message || `"${a.title}" is back to Omni\u2019s version.`);
          if (res?.article) setArticle(res.article);
        } catch (err) {
          toast.error('Could not restore', err?.message || 'Please try again.');
        }
      }
    });
  }

  const showingSearch = results !== null;

  return createPortal(
    <div
      className="help-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="help-panel" role="dialog" aria-modal="true" aria-label="Help Center">
        {/* Top bar */}
        <div className="help-topbar">
          <div className="help-topbar-icon">
            <i className="fas fa-life-ring" aria-hidden="true" />
          </div>
          <div>
            <h2>Help Center</h2>
            <p>Step-by-step guides for everything in Omni — and a place to report problems.</p>
          </div>
          <div className="help-topbar-spacer" />
          <button className="help-close" onClick={close} aria-label="Close help">
            &times;
          </button>
        </div>

        <div className="help-body">
          {/* Left rail */}
          <div className="help-rail">
            <div className="help-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Ask a question or search guides…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Ask a question or search guides"
              />
            </div>
            <div className="help-rail-scroll">
              <div className="help-rail-label">Topics</div>
              {sections.map((s) => (
                <button
                  key={s.id}
                  className={`help-section-item${!showingSearch && view !== 'feedback' && s.id === activeSection ? ' active' : ''}`}
                  onClick={() => {
                    setQuery('');
                    setResults(null);
                    setEditing(null);
                    goToSection(s.id);
                  }}
                >
                  <span className="help-section-tile" style={{ background: s.gradient || 'linear-gradient(135deg,#667eea,#764ba2)' }}>
                    <i className={`fas ${s.icon}`} aria-hidden="true" />
                  </span>
                  <span>
                    <span className="help-section-name">{s.name}</span>
                    <span className="help-section-desc">{s.description}</span>
                  </span>
                </button>
              ))}
            </div>
            <div className="help-rail-actions">
              {isAdmin && (
                <button
                  className="help-rail-btn"
                  onClick={() => {
                    setEditing({ create: true });
                  }}
                >
                  <i className="fas fa-plus" aria-hidden="true" /> New article
                </button>
              )}
              <button
                className="help-rail-btn primary"
                onClick={() => {
                  setEditing(null);
                  setFeedbackType('bug');
                  goToFeedback();
                }}
              >
                <i className="fas fa-comment-dots" aria-hidden="true" /> Report a problem
              </button>
            </div>
          </div>

          {/* Main pane */}
          <div className="help-main">
            {editing ? (
              <ArticleEditor
                sections={sections}
                article={editing.article}
                defaultSection={activeSection}
                onCancel={() => setEditing(null)}
                onSaved={(saved) => {
                  setEditing(null);
                  goToArticle(saved.id, saved.section);
                }}
              />
            ) : showingSearch ? (
              <>
                {/* Answers the question above the raw matches: describing a task
                    in prose rarely shares words with the guide that covers it. */}
                <AskOmni query={query} />
                <SearchResults query={query} results={results} sections={sections} onOpen={openArticleFromResult} />
              </>
            ) : view === 'feedback' ? (
              <>
                <div className="help-main-header">
                  <h3>Report a problem or request a feature</h3>
                  <p>We'll email the IT team and log it so it isn't forgotten.</p>
                </div>
                <FeedbackForm
                  sections={sections}
                  defaultSection={activeSection}
                  defaultType={feedbackType}
                  onDone={() => goToSection(activeSection)}
                />
              </>
            ) : view === 'article' ? (
              <ArticleView
                loading={loading}
                article={article}
                sectionName={sections.find((s) => s.id === article?.section)?.name}
                isAdmin={isAdmin}
                onBack={() => goToSection(article?.section || activeSection)}
                onEdit={() => setEditing({ article })}
                onDelete={() => article && removeArticle(article)}
                onReset={() => article && resetArticle(article)}
                onReport={() => {
                  setFeedbackType('bug');
                  goToFeedback();
                }}
              />
            ) : (
              <BrowseView
                loading={loading}
                meta={sectionMeta}
                articles={articles}
                onOpen={(a) => goToArticle(a.id, a.section)}
              />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function BrowseView({ loading, meta, articles, onOpen }) {
  return (
    <>
      <div className="help-main-header">
        <h3>{meta?.name || 'Guides'}</h3>
        <p>{meta?.description}</p>
      </div>
      {loading ? (
        <div className="help-loading">
          <i className="fas fa-spinner help-spin" aria-hidden="true" /> Loading guides…
        </div>
      ) : articles.length === 0 ? (
        <div className="help-empty">
          <i className="fas fa-book-open" aria-hidden="true" />
          <div>No guides here yet.</div>
        </div>
      ) : (
        <div className="help-article-cards">
          {articles.map((a) => (
            <button key={a.id} className="help-article-card" onClick={() => onOpen(a)}>
              <h4>
                {a.title}
                {a.published === false && <span className="help-draft-badge">Draft</span>}
              </h4>
              {a.summary && <p>{a.summary}</p>}
              {(a.tags || []).slice(0, 4).map((t) => (
                <span key={t} className="help-tag">
                  {t}
                </span>
              ))}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function SearchResults({ query, results, sections, onOpen }) {
  return (
    <>
      <div className="help-main-header">
        <h3>Results for "{query}"</h3>
        <p>{results.length === 0 ? 'Nothing matched — try different words.' : `${results.length} guide${results.length === 1 ? '' : 's'} found.`}</p>
      </div>
      {results.length === 0 ? (
        <div className="help-empty">
          <i className="fas fa-magnifying-glass" aria-hidden="true" />
          <div>No matching guides.</div>
        </div>
      ) : (
        <div className="help-article-cards">
          {results.map((r) => (
            <button key={r.id} className="help-article-card" onClick={() => onOpen(r)}>
              <h4>{r.title}</h4>
              <p>{r.snippet}</p>
              <span className="help-tag">{sections.find((s) => s.id === r.section)?.name || r.section}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function ArticleView({ loading, article, sectionName, isAdmin, onBack, onEdit, onDelete, onReset, onReport }) {
  // A shipped guide that someone has edited here: saveArticle() stamps
  // updatedBy/updatedAt, so those marks are what distinguish it.
  const customised = Boolean(article?.builtIn && (article.updatedBy || article.updatedAt));
  if (loading) {
    return (
      <div className="help-loading">
        <i className="fas fa-spinner help-spin" aria-hidden="true" /> Loading…
      </div>
    );
  }
  if (!article) {
    return (
      <div className="help-empty">
        <i className="fas fa-circle-question" aria-hidden="true" />
        <div>That guide could not be found.</div>
        <button className="help-mini-btn" style={{ marginTop: 14 }} onClick={onBack}>
          Back
        </button>
      </div>
    );
  }
  return (
    <div>
      <div className="help-breadcrumb">
        <button type="button" onClick={onBack}>
          <i className="fas fa-arrow-left" aria-hidden="true" /> {sectionName || 'Back'}
        </button>
      </div>
      <div className="help-main-header">
        <h3>{article.title}</h3>
        {article.summary && <p>{article.summary}</p>}
      </div>
      {isAdmin && (
        <>
          <div className="help-article-toolbar">
            <button className="help-mini-btn" onClick={onEdit}>
              <i className="fas fa-pen" aria-hidden="true" /> Edit
            </button>
            {customised && (
              <button
                className="help-mini-btn"
                onClick={onReset}
                title="Replace this with the version shipped in this build, and let it update automatically again"
              >
                <i className="fas fa-rotate-left" aria-hidden="true" /> Restore Omni&apos;s version
              </button>
            )}
            <button className="help-mini-btn danger" onClick={onDelete}>
              <i className="fas fa-trash" aria-hidden="true" /> Delete
            </button>
          </div>
          {customised && (
            <div style={{
              padding: '9px 12px', marginBottom: 12, borderRadius: 8, fontSize: 12,
              background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)'
            }}>
              <strong>Edited here.</strong> Because this guide has been changed locally, improvements shipped
              with an Omni update are not applied to it. Restore Omni&apos;s version to opt back in.
            </div>
          )}
        </>
      )}
      <Markdown>{article.content}</Markdown>
      <div className="help-article-meta">
        <button
          className="help-mini-btn"
          onClick={onReport}
          style={{ marginBottom: 10 }}
        >
          <i className="fas fa-comment-dots" aria-hidden="true" /> Something wrong on this page? Tell us
        </button>
        {article.updatedAt && <div>Last updated {new Date(article.updatedAt).toLocaleDateString('en-GB')}.</div>}
      </div>
    </div>
  );
}
